import { MouseEvent, useCallback, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { useQueryParams } from "@shared/hooks";
import { FilterDropdownProps } from "./types";

export function useFilterDropdownController({
  initialValues,
  paramPrefix = "",
}: Omit<FilterDropdownProps, "children">) {
  const { getQueryParams, setQueryParams } = useQueryParams();

  const urlKey = useCallback(
    (key: string) => `${paramPrefix}${key}`,
    [paramPrefix]
  );

  const valuesFromUrl = useCallback(() => {
    const current = getQueryParams();
    if (!paramPrefix) return { ...initialValues, ...current };

    const values = { ...initialValues };
    for (const key of Object.keys(initialValues)) {
      const stored = current[urlKey(key)];
      if (stored !== undefined) values[key] = stored;
    }
    return values;
  }, [getQueryParams, initialValues, paramPrefix, urlKey]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const methods = useForm({
    defaultValues: valuesFromUrl(),
  });

  const { handleSubmit: hookHandleFormSubmit, reset } = methods;

  const dropdownKeys = useMemo(
    () => Object.keys(initialValues),
    [initialValues]
  );

  const hasActiveFilters = useMemo(() => {
    const current = getQueryParams();
    return dropdownKeys.some((key) => Boolean(current[urlKey(key)]));
  }, [dropdownKeys, getQueryParams, urlKey]);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // Re-sync the form with the URL so the popover never shows stale
      // typed-but-uncommitted values or filters changed elsewhere.
      reset(valuesFromUrl());
      setAnchorEl(event.currentTarget);
    },
    [reset, valuesFromUrl]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleReset = useCallback(() => {
    reset(initialValues);
    const current = getQueryParams();
    const storedKeys = new Set(Object.keys(initialValues).map((key) => urlKey(key)));
    setQueryParams(
      Object.fromEntries(
        Object.entries(current).filter(
          ([key]) => !storedKeys.has(key) && key !== "page"
        )
      )
    );
    handleClose();
  }, [initialValues, reset, getQueryParams, setQueryParams, handleClose, urlKey]);

  const handleSubmit = useCallback<SubmitHandler<Record<string, any>>>(
    (formData) => {
      if (!paramPrefix) {
        setQueryParams({
          ...getQueryParams(),
          ...formData,
          page: "1",
        });
        handleClose();
        return;
      }

      const next = { ...getQueryParams() };
      for (const key of Object.keys(initialValues)) {
        const value = formData[key];
        if (value === undefined || value === null || value === "") {
          delete next[urlKey(key)];
        } else {
          next[urlKey(key)] = value;
        }
      }
      setQueryParams(next);
      handleClose();
    },
    [paramPrefix, setQueryParams, handleClose, getQueryParams, initialValues, urlKey]
  );

  return {
    methods,
    anchorEl,
    open: Boolean(anchorEl),
    hasActiveFilters,
    handleClick,
    handleClose,
    handleReset,
    handleSubmit: hookHandleFormSubmit(handleSubmit),
  };
}
